import { BriefcaseBusiness, Check, ShieldCheck, UserRound } from "lucide-react";
import { useLocation } from "react-router";
import { Eyebrow } from "@zylarity/ui";
import {
  InquiryButtonLink,
  inquiryOrigin,
  inquiryService,
  inquiryEntryLink,
  withInquiryOrigin,
} from "../inquiry-origin.js";
import "../interior-redesign.css";
import "./contact.css";

export function ContactPage() {
  const { search } = useLocation();
  const params = new URLSearchParams(search);
  const origin = inquiryOrigin(params.get("from"));
  const via = inquiryEntryLink(params.get("via"));
  const service = inquiryService(params);
  const audience =
    params.get("kind") === "PROFESSIONAL"
      ? "candidate"
      : ["EMPLOYER", "ASSESSMENT"].includes(params.get("kind") ?? "") || service
        ? "client"
        : undefined;
  const destination = (kind: "candidate" | "client") =>
    withInquiryOrigin(
      `/intake/${kind}?intro=1${kind === "client" && service ? `&service=${service}` : ""}`,
      origin ?? "/contact",
      via ?? `get-started-${kind}`,
    );
  return (
    <div className="interior-redesign get-started">
      <section className="public-shell get-started__heading">
        <Eyebrow>A useful next conversation</Eyebrow>
        <h1>Let’s start with the work</h1>
        <p>
          Find your next opportunity or the expertise your team needs. Choose your path and tell us
          a little about what comes next for you.
        </p>
      </section>
      <section
        className="public-shell get-started__choices"
        aria-label="Choose how to get started"
        data-audience={audience}
      >
        <article
          className="get-started__card"
          data-kind="candidate"
          data-selected={audience === "candidate"}
        >
          <span className="get-started__icon">
            <UserRound aria-hidden="true" />
          </span>
          <Eyebrow>For professionals</Eyebrow>
          <h2>I’m looking for work</h2>
          <p>
            Bring your Cybersecurity and OT experience. Tell us what you want from your next role.
          </p>
          <ul>
            <li>
              <Check aria-hidden="true" /> Start with a short introduction
            </li>
            <li>
              <Check aria-hidden="true" /> Add a resume and preferences when you’re ready
            </li>
            <li>
              <Check aria-hidden="true" /> Choose how we may contact you
            </li>
          </ul>
          <InquiryButtonLink href={destination("candidate")} via={via ?? "get-started-candidate"}>
            Start my introduction
          </InquiryButtonLink>
        </article>
        <article
          className="get-started__card"
          data-kind="client"
          data-selected={audience === "client"}
        >
          <span className="get-started__icon">
            <BriefcaseBusiness aria-hidden="true" />
          </span>
          <Eyebrow>For hiring teams</Eyebrow>
          <h2>I’m hiring</h2>
          {service && (
            <p className="get-started__context">
              You selected{" "}
              {service === "talent-assessments"
                ? "Talent Assessments"
                : "Secure Job Description Development"}
              . You can edit your request in the introduction.
            </p>
          )}
          <p>
            Tell us about the work and the people you need. We’ll start a focused hiring
            conversation.
          </p>
          <ul>
            <li>
              <Check aria-hidden="true" /> Share the need in your own words
            </li>
            <li>
              <Check aria-hidden="true" /> No finished job description required
            </li>
            <li>
              <Check aria-hidden="true" /> Add company and role details at your pace
            </li>
          </ul>
          <InquiryButtonLink href={destination("client")} via={via ?? "get-started-client"}>
            Start a hiring conversation
          </InquiryButtonLink>
        </article>
      </section>
      <div className="public-shell get-started__assurance">
        <ShieldCheck aria-hidden="true" size={22} />
        <p>Your introduction reaches our team before you complete the longer intake.</p>
      </div>
    </div>
  );
}
